// src/services/realtimeService.js - Production Pub/Sub Client
import { WS_URL } from '../config.js';
class RealtimeService {
  constructor() {
    this.ws = null;
    this.subscriptions = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.isConnected = false;
    this.isAuthenticated = false;
    this.connectionListeners = [];
    this._connectPromise = null;
  }

  connect(token) {
    if (this.isAuthenticated && this.ws?.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    if (this._connectPromise) {
      return this._connectPromise;
    }

    this._connectPromise = new Promise((resolve, reject) => {
      const wsBaseUrl = WS_URL;

      console.log('[RT] Connecting to', wsBaseUrl);
      this.ws = new WebSocket(wsBaseUrl);

      const authTimeout = setTimeout(() => {
        this._connectPromise = null;
        reject(new Error('Authentication timeout'));
        this.ws.close();
      }, 10000);

      this.ws.onopen = () => {
        console.log('[RT] WebSocket connected, authenticating...');
        this.ws.send(JSON.stringify({
          type: 'CLIENT_AUTH',
          token: token
        }));
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'AUTH_OK') {
            clearTimeout(authTimeout);
            this.isConnected = true;
            this.isAuthenticated = true;
            this.reconnectAttempts = 0;
            this._connectPromise = null;
            this._notifyListeners('connected');

            // Re-subscribe all active subscriptions (reconnection case)
            for (const [subId, sub] of this.subscriptions.entries()) {
              this._sendSubscribe(subId, sub);
            }

            console.log('[RT] Authenticated successfully');
            resolve();
            return;
          }

          if (data.type === 'AUTH_ERROR') {
            clearTimeout(authTimeout);
            this.isAuthenticated = false;
            this._connectPromise = null;
            reject(new Error(data.error || 'Authentication failed'));
            return;
          }

          if (data.type === 'SUBSCRIBE_OK') {
            const sub = this.subscriptions.get(data.subscriptionId);
            if (sub) sub.confirmed = true;
            return;
          }

          if (data.type === 'QUERY_UPDATE') {
            const sub = this.subscriptions.get(data.subscriptionId);
            if (sub && sub.callback) {
              sub.callback({
                columns: data.columns,
                dataset: data.dataset,
                timestamp: data.timestamp
              });
            }
            return;
          }

          if (data.type === 'QUERY_ERROR') {
            const sub = this.subscriptions.get(data.subscriptionId);
            if (sub && sub.errorCallback) {
              sub.errorCallback(data.error);
            }
            return;
          }

        } catch (err) {
          console.error('[RT] Message parse error:', err);
        }
      };

      this.ws.onclose = (event) => {
        clearTimeout(authTimeout);
        this.isConnected = false;
        this.isAuthenticated = false;
        this._connectPromise = null;
        this._notifyListeners('disconnected');

        // Don't reconnect on auth failure (code 4001)
        if (event.code !== 4001) {
          this._reconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error('[RT] WebSocket error:', error);
      };
    });

    return this._connectPromise;
  }

  /**
   * Subscribe to a SQL query for live updates
   * @param {Object} options
   * @param {string} options.sql - The SQL query
   * @param {string} options.plantId - The plant ID
   * @param {number} options.intervalMs - Refresh interval in milliseconds
   * @param {function} options.onData - Callback: (result) => void
   * @param {function} options.onError - Error callback: (errorMessage) => void
   * @returns {string} subscriptionId
   */
  subscribe({ sql, plantId, intervalMs, onData, onError, dbEngine, mysqlDatabase }) {
    const subscriptionId = `sub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const subInfo = {
      sql,
      plantId,
      intervalMs: intervalMs || 5000,
      dbEngine: dbEngine || 'questdb',
      mysqlDatabase: mysqlDatabase || null,
      callback: onData,
      errorCallback: onError,
      confirmed: false
    };

    this.subscriptions.set(subscriptionId, subInfo);

    if (this.isAuthenticated && this.ws?.readyState === WebSocket.OPEN) {
      this._sendSubscribe(subscriptionId, subInfo);
    }

    return subscriptionId;
  }

  _sendSubscribe(subscriptionId, subInfo) {
    this.ws.send(JSON.stringify({
      type: 'SUBSCRIBE',
      subscriptionId,
      sql: subInfo.sql,
      plantId: subInfo.plantId,
      intervalMs: subInfo.intervalMs,
      dbEngine: subInfo.dbEngine,
      mysqlDatabase: subInfo.mysqlDatabase
    }));
  }

  unsubscribe(subscriptionId) {
    if (!subscriptionId) return;

    this.subscriptions.delete(subscriptionId);

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: 'UNSUBSCRIBE',
        subscriptionId
      }));
    }
  }

  onStatusChange(callback) {
    this.connectionListeners.push(callback);
    return () => {
      this.connectionListeners = this.connectionListeners.filter(cb => cb !== callback);
    };
  }

  _notifyListeners(status) {
    for (const cb of this.connectionListeners) {
      try { cb(status); } catch (e) { /* ignore */ }
    }
  }

  _reconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[RT] Max reconnection attempts reached');
      this._notifyListeners('failed');
      return;
    }

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectAttempts++;

    console.log(`[RT] Reconnecting in ${delay / 1000}s (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    setTimeout(() => {
      const token = localStorage.getItem('token');
      if (token) {
        this.connect(token).catch(err => {
          console.error('[RT] Reconnection failed:', err.message);
        });
      }
    }, delay);
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.isConnected = false;
      this.isAuthenticated = false;
    }
  }

  isReady() {
    return this.isConnected && this.isAuthenticated && this.ws?.readyState === WebSocket.OPEN;
  }

  // Format data to match questDBService format
  formatData(response) {
    const { columns, dataset } = response;

    if (!dataset || !columns) {
      return [];
    }

    return dataset.map(row => {
      const obj = {};
      columns.forEach((col, index) => {
        let value = Array.isArray(row) ? row[index] : row[col.name];

        if (col.type === 'TIMESTAMP') {
          value = this.formatTimestamp(value);
        }

        obj[col.name] = value;
      });
      return obj;
    });
  }

  formatTimestamp(timestamp) {
    if (!timestamp) return null;

    let date;

    if (typeof timestamp === 'number') {
      if (timestamp > 1000000000000000) {
        date = new Date(timestamp / 1000);
      } else if (timestamp > 1000000000000) {
        date = new Date(timestamp);
      } else {
        date = new Date(timestamp * 1000);
      }
    } else {
      date = new Date(timestamp);
    }

    if (isNaN(date.getTime())) {
      return timestamp;
    }

    return date.toISOString();
  }

  formatForChart(data, timestampField = 'timestamp', valueField = 'value') {
    return data.map(item => {
      const timestamp = item[timestampField];
      let displayTime = 'N/A';

      if (timestamp) {
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          displayTime = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          });
        }
      }

      return {
        ...item,
        _time: displayTime,
        _timestamp: timestamp,
        _value: item[valueField]
      };
    }).reverse();
  }
}

export default new RealtimeService();
